import { createPerfilApiClient } from './perfilApiClient';
import { toObject, toArray, toPaginated } from './_normalize';

/**
 * Invitaciones de Abonado — Service
 *
 * Métodos puros para todas las llamadas HTTP del flujo de invitaciones de abonado.
 * Usa `createPerfilApiClient()` para autenticación y gestión de cabeceras.
 *
 * Contrato de retorno:
 *  - Todos los métodos devuelven datos ya normalizados (via `_normalize.js`).
 *  - Los errores NO se capturan aquí; se propagan al composable que los
 *    convierte con `toError()` para exponer `loading/error` consistentes.
 *
 * Endpoints base: /f/perfil/abono/{abonado_id}/invitaciones-abonado/...
 * Borrado de sesión:  DELETE /f/sesion/{session_id}
 */

const BASE = abonadoId => `/f/perfil/abono/${abonadoId}/invitaciones-abonado`;

/**
 * Lee el api_key del localStorage en el momento de la llamada.
 * @returns {string|null}
 */
function getApiKey() {
    return localStorage.getItem('token');
}

export default {
    /**
     * Obtiene las estadísticas de invitaciones del abonado para la temporada.
     * Total, consumidas y restantes.
     *
     * GET /f/perfil/abono/{abonado_id}/invitaciones-abonado
     *
     * @param {string|number} abonadoId
     * @returns {Promise<{ total: number, consumidas: number, restantes: number }>}
     */
    async getStats(abonadoId) {
        const client = createPerfilApiClient();
        const response = await client.get(BASE(abonadoId), {
            params: { api_key: getApiKey() },
        });
        const raw = toObject(response.data);
        const src = raw.invitaciones_abonado ?? raw;
        return {
            total:     src.n_total_temporada  ?? 0,
            consumidas: src.n_total_consumidas ?? 0,
            restantes:  src.n_total_disponibles ?? 0,
        };
    },

    /**
     * Obtiene el listado de eventos disponibles para generar invitaciones
     * con el abono indicado.
     *
     * GET /f/perfil/abono/{abonado_id}/invitaciones-abonado/eventos
     *
     * @param {string|number} abonadoId
     * @returns {Promise<Array>}
     */
    async getEventos(abonadoId) {
        const client = createPerfilApiClient();
        const response = await client.get(`${BASE(abonadoId)}/eventos`, {
            params: { api_key: getApiKey() },
        });
        return toArray(response.data).map(e => ({
            ...e,
            nombre:     e.nombre     ?? e.name,
            // Normalizar los identificadores que el composable usa para construir URLs.
            // El backend puede devolver el id del evento como `id` o como `evento_id`.
            evento_id:  e.evento_id  ?? e.id,
            sesion_id:  e.sesion_id  ?? e.session_id ?? null,
        }));
    },

    /**
     * Obtiene el historial paginado de invitaciones ya consumidas por el abonado.
     *
     * GET /f/perfil/abono/{abonado_id}/invitaciones-abonado/consumidas
     *
     * @param {string|number} abonadoId
     * @param {{ page?: number, per_page?: number }} params
     * @returns {Promise<{ items: Array, meta: { current_page: number, last_page: number, total: number, per_page: number } }>}
     */
    async getConsumidasPaginadas(abonadoId, { page = 1, per_page = 10 } = {}) {
        const client = createPerfilApiClient();
        const response = await client.get(`${BASE(abonadoId)}/consumidas`, {
            params: { api_key: getApiKey(), page, per_page },
        });
        const paginated = toPaginated(response.data);
        return {
            ...paginated,
            items: paginated.items.map(item => ({
                id:             item.id,
                evento_nombre:  item.evento?.name  ?? item.evento_nombre ?? '—',
                fecha_consumo:  item.created_at    ?? item.fecha_consumo ?? null,
                invitado_nombre: item.user
                    ? `${item.user.name ?? ''} ${item.user.apellidos ?? ''}`.trim()
                    : (item.invitado_nombre ?? null),
                documento:      item.user?.dni      ?? item.documento    ?? null,
                asiento:        item.asiento        ?? null,
            })),
        };
    },

    /**
     * Obtiene las zonas disponibles para la sesión de un evento concreto.
     *
     * GET /f/perfil/abono/{abonado_id}/invitaciones-abonado/evento/{evento_id}/{sesion_id}/zonas
     *
     * @param {string|number} abonadoId
     * @param {string|number} eventoId
     * @param {string|number} sesionId
     * @returns {Promise<Array>}
     */
    async getZonas(abonadoId, eventoId, sesionId, mostrarPrecioMasAlto = false) {
        const client = createPerfilApiClient();
        const response = await client.get(
            `${BASE(abonadoId)}/evento/${eventoId}/${sesionId}/zonas`,
            { params: { api_key: getApiKey() } }
        );
        return toArray(response.data).map(z => {
            // Precio: el backend devuelve `precios` (array de objetos { precio })
            // o un campo plano `price`/`precio`.
            // Equivalente al TablaSectoresComponent legacy:
            //   - mostrar_precio_mas_alto_en_la_zona = false → precios[0] (primero, mínimo)
            //   - mostrar_precio_mas_alto_en_la_zona = true  → precios[last] (último, máximo)
            let price = z.price ?? z.precio ?? null;
            if (price == null && Array.isArray(z.precios) && z.precios.length > 0) {
                const idx = mostrarPrecioMasAlto ? z.precios.length - 1 : 0;
                price = z.precios[idx].precio ?? null;
            }

            // Disponibilidad: el backend devuelve `libres` (legacy) o `availability`.
            // Si `with_seats === 0` la zona no tiene butacas numeradas → sin contador.
            let availability = z.availability ?? z.libres ?? null;
            if (z.with_seats === 0) {
                availability = null;
            }

            return {
                ...z,
                // El backend devuelve `esta_disponible`; ZoneTable espera `available`.
                available:    z.available ?? Boolean(z.esta_disponible),
                name:         z.name      ?? z.nombre ?? z.descripcion ?? '',
                price,
                availability,
            };
        });
    },

    /**
     * Obtiene los asientos disponibles en una zona concreta para la sesión del evento.
     *
     * GET /f/perfil/abono/{abonado_id}/invitaciones-abonado/evento/{evento_id}/{sesion_id}/zonas/{zona_id}/asientos
     *
     * @param {string|number} abonadoId
     * @param {string|number} eventoId
     * @param {string|number} sesionId
     * @param {string|number} zonaId
     * @returns {Promise<Object>} Datos de asientos con mapa SVG y metadatos de sector
     */
    async getAsientos(abonadoId, eventoId, sesionId, zonaId) {
        const client = createPerfilApiClient();
        const response = await client.get(
            `${BASE(abonadoId)}/evento/${eventoId}/${sesionId}/zonas/${zonaId}/asientos`,
            { params: { api_key: getApiKey() } }
        );
        return toObject(response.data);
    },

    /**
     * Bloquea temporalmente los asientos seleccionados y crea la sesión de compra.
     * En caso de error (asiento ocupado por otro usuario), el composable debe
     * capturar la excepción con `toError()`, mostrar un toast y limpiar la selección local.
     *
     * POST /f/perfil/abono/{abonado_id}/invitaciones-abonado/evento/{evento_id}/{sesion_id}/bloquear-asientos
     *
     * @param {string|number} abonadoId
     * @param {string|number} eventoId
     * @param {string|number} sesionId
     * @param {{ filters: Object, abonado_id: string, evento_id: string, sesion_id: string, asientos: Array<{ fila: string, asiento: string|number, zona_id: number|null, zona_nombre: string }> }} payload
     * @returns {Promise<{ session_id: string, asientos: Array }>}
     */
    async bloquearAsientos(abonadoId, eventoId, sesionId, payload) {
        const client = createPerfilApiClient();
        const response = await client.post(
            `${BASE(abonadoId)}/evento/${eventoId}/${sesionId}/bloquear-asientos`,
            payload,
            { params: { api_key: getApiKey() } }
        );
        return toObject(response.data);
    },

    /**
     * Guarda los datos del comprador principal y, si aplica, los datos nominativos
     * por butaca (cuando `evento.solicitar_datos === true`) y los campos custom del evento.
     *
     * POST /f/perfil/abono/{abonado_id}/invitaciones-abonado/evento/{evento_id}/{sesion_id}/guardar-datos-comprador
     *
     * @param {string|number} abonadoId
     * @param {string|number} eventoId
     * @param {string|number} sesionId
     * @param {Object} payload Datos del comprador, campos custom y datos por butaca
     * @returns {Promise<{ amount: number, jet_id?: string|null, formulario?: string|null }>}
     */
    async guardarDatosComprador(abonadoId, eventoId, sesionId, payload) {
        const client = createPerfilApiClient();
        const response = await client.post(
            `${BASE(abonadoId)}/evento/${eventoId}/${sesionId}/guardar-datos-comprador`,
            payload,
            { params: { api_key: getApiKey() } }
        );
        return toObject(response.data);
    },

    /**
     * Procesa la compra de invitaciones a coste cero (sin pasarela de pago).
     * Solo debe llamarse cuando `amount === 0` y no existe `jet_id`.
     * En caso de éxito devuelve los identificadores para redirigir a la pantalla
     * de compra exitosa: /compra/{session_id}/{n_pedido}
     *
     * POST /f/perfil/abono/{abonado_id}/invitaciones-abonado/evento/{evento_id}/{sesion_id}/procesar-compra
     *
     * Payload idéntico al legacy (InvitacionesEventos.vue → procesarCompraPrecioCero):
     *   { filters: {}, abonado_id, evento_id, sesion_id, session_id }
     *
     * @param {string|number} abonadoId
     * @param {string|number} eventoId
     * @param {string|number} sesionId
     * @param {string} sessionId  ID de sesión del bloqueo activo (requerido por el backend)
     * @returns {Promise<{ session_id: string, n_pedido: string|number }>}
     */
    async procesarCompraSinGG(abonadoId, eventoId, sesionId, sessionId) {
        const client = createPerfilApiClient();
        const response = await client.post(
            `${BASE(abonadoId)}/evento/${eventoId}/${sesionId}/procesar-compra`,
            {
                filters:    {},
                abonado_id: String(abonadoId),
                evento_id:  String(eventoId),
                sesion_id:  String(sesionId),
                session_id: String(sessionId ?? ''),
            },
            { params: { api_key: getApiKey() } }
        );
        return toObject(response.data);
    },

    /**
     * Obtiene el SVG del recinto (mapa de zonas) para mostrarlo en el Paso 1.
     * El endpoint público es /f/recinto/{recinto_id} y devuelve un campo `svg`.
     *
     * GET /f/recinto/{recinto_id}
     *
     * @param {string|number} recintoId  ID del recinto (place.id del evento)
     * @returns {Promise<string>}  String SVG del mapa del recinto
     */
    async getSvgRecinto(recintoId) {
        const client = createPerfilApiClient();
        const hashTeatro = localStorage.getItem('hash_teatro');
        const response = await client.get(`/f/recinto/${recintoId}`, {
            params: {
                api_key: getApiKey(),
                tid: hashTeatro,
            },
        });
        return response.data?.svg ?? response.data?.data?.svg ?? '';
    },

    /**
     * Borra la sesión activa del backend para liberar los asientos bloqueados.
     * Debe llamarse cuando el usuario retrocede del Paso 3 al Paso 2 o cuando
     * el temporizador de sesión expira.
     *
     * DELETE /f/sesion/{session_id}
     *
     * @param {string} sessionId Identificador de sesión retornado por bloquearAsientos
     * @returns {Promise<void>}
     */
    async borrarSesion(sessionId) {
        const client = createPerfilApiClient();
        await client.delete(`/f/sesion/${sessionId}`, {
            params: { api_key: getApiKey() },
        });
    },

    // ── Paycomet ──────────────────────────────────────────────────────────────

    /**
     * Procesa el pago con Paycomet para la sesión de compra activa.
     *
     * POST /f/paycomet/evento/{session_id}/procesar-pago-paycomet
     *
     * @param {string} sessionId ID de sesión del bloqueo activo
     * @param {string} jetToken  Token de pago generado por el iframe de Paycomet
     * @returns {Promise<import('axios').AxiosResponse>}
     */
    async procesarPagoPaycomet(sessionId, jetToken) {
        const client = createPerfilApiClient();
        return client.post(
            `/f/paycomet/evento/${sessionId}/procesar-pago-paycomet`,
            {
                jetToken,
                session_id: sessionId,
                tipo_venta: 'evento',
            },
            { params: { api_key: getApiKey() } }
        );
    },

    /**
     * Verifica si el pedido asociado a la sesión ya fue procesado con éxito.
     *
     * POST /f/paycomet/pedidos/{session_id}/is-procesado
     *
     * @param {string} sessionId ID de sesión
     * @returns {Promise<import('axios').AxiosResponse>}
     */
    async checkPedidoProcesado(sessionId) {
        const client = createPerfilApiClient();
        return client.post(
            `/f/paycomet/pedidos/${sessionId}/is-procesado`,
            {
                session_id: sessionId,
                tipo_pedido: 'evento',
            },
            { params: { api_key: getApiKey() } }
        );
    },
};
